class AccountDrawer extends HTMLElement {
  constructor() {
    super();
  }

  get selectors() {
    return {
      drawer: {
        button: {
          open: "[data-account-drawer-open]",
          close: "[data-account-drawer-close]"
        },
        scrollableContent: "[data-account-drawer-scrollable]"
      },
      tieredMenu: {
        container: "[data-account-drawer-tier]",
        trigger: "[data-account-drawer-tier-trigger]",
        backButton: "[data-account-drawer-tier-back-button]"
      }
    }
  }

  get modifiers() {
    return {
      drawer: {
        open: "drawer--open"
      },
      tieredMenu: {
        open: "account-drawer__navigation-tier--open"
      },
      button: {
        open: {
          pulse: "header__circle-icon--pulse"
        }
      }
    }
  }

  connectedCallback() {
    this.initTieredMenu();

    document.documentElement.customDelegate(
      "click",
      this.selectors.drawer.button.open,
      this.openDrawer.bind(this)
    );

    document.documentElement.customDelegate(
      "click",
      this.selectors.drawer.button.close,
      this.closeDrawer.bind(this)
    );

    document.addEventListener("account-drawer:open", this.openDrawer.bind(this));
    document.addEventListener("account-drawer:close", this.closeDrawer.bind(this));

    window.addEventListener("resize", this.setDrawerHeight.debounce(50).bind(this));
  }

  disconnectedCallback() {
    this.destroyTieredMenu();

    document.removeEventListener("account-drawer:open", this.openDrawer.bind(this));
    document.removeEventListener("account-drawer:close", this.closeDrawer.bind(this));

    window.removeEventListener("resize", this.setDrawerHeight.debounce(50).bind(this));
  }

  openDrawer(event) {
    event.preventDefault();

    const scrollableContent = this.querySelector(this.selectors.drawer.scrollableContent);

    bodyScrollLock.disableBodyScroll(scrollableContent);

    this.classList.add(this.modifiers.drawer.open);

    this.focusDrawer();

    document.querySelector(this.selectors.drawer.button.open).classList.remove(this.modifiers.button.open.pulse);
  }

  closeDrawer(event) {
    event.preventDefault();

    const scrollableContent = this.querySelector(this.selectors.drawer.scrollableContent);

    this.classList.remove(this.modifiers.drawer.open);

    this.closeAllTieredMenus();

    bodyScrollLock.clearAllBodyScrollLocks();

    this.returnFocusToPreviousElement();
  }

  setDrawerHeight() {
    const drawerHeight = window.innerHeight;

    this.style.setProperty(
      '--drawer-height',
      `${drawerHeight}px`
    );
  }

  initTieredMenu() {
    document.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        this.openTieredMenu(event);
      }
    });

    document.documentElement.customDelegate(
      "click",
      this.selectors.tieredMenu.trigger,
      this.openTieredMenu.bind(this)
    );

    document.documentElement.customDelegate(
      "click",
      this.selectors.tieredMenu.backButton,
      this.closeTieredMenu.bind(this)
    );
  }

  destroyTieredMenu() {
    document.removeEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        this.openTieredMenu(event);
      }
    });

    document.documentElement.removeCustomDelegate(
      "click",
      this.selectors.tieredMenu.trigger,
      this.openTieredMenu.bind(this)
    );

    document.documentElement.removeCustomDelegate(
      "click",
      this.selectors.tieredMenu.backButton,
      this.closeTieredMenu.bind(this)
    );
  }

  openTieredMenu(event) {
    const trigger = event?.target?.closest(this.selectors.tieredMenu.trigger);

    if (!trigger) {
      return false;
    }

    event.preventDefault();

    const menuId = trigger.getAttribute("data-account-drawer-tier-trigger");

    const menu = document.querySelector(`[data-account-drawer-tier='${menuId}']`); // TODO

    if (!menu) {
      return false;
    }

    bodyScrollLock.disableBodyScroll(menu);

    menu.classList.add(this.modifiers.tieredMenu.open);

    const focusableElements = menu.getFocusableElements();
    const firstFocusableElement = focusableElements[0];

    setTimeout(() => {
      firstFocusableElement.focus();
    }, 400);

    menu.trapFocus();

    menu.removeAttribute("inert");
  }

  closeTieredMenu(event) {
    const backButton = event?.target?.closest(this.selectors.tieredMenu.backButton);

    if (!backButton) {
      return false;
    }

    const menu = backButton.closest(this.selectors.tieredMenu.container); // TODO

    if (!menu) {
      return false;
    }

    menu.removeTrapFocus();

    menu.classList.remove(this.modifiers.tieredMenu.open);

    menu.setAttribute("aria-expanded", "false");

    bodyScrollLock.enableBodyScroll(menu);
  }

  closeAllTieredMenus() {
    const tieredMenus = [
      ...this.querySelectorAll(this.selectors.tieredMenu.container)
    ];

    if (!tieredMenus.length) {
      return false;
    }

    tieredMenus.forEach((tieredMenu) => {
      tieredMenu.classList.remove(this.modifiers.tieredMenu.open);

      tieredMenu.setAttribute("aria-expanded", "false");

      bodyScrollLock.enableBodyScroll(tieredMenu);
    });
  }

  focusDrawer() {
    this.previouslyFocusedElement = document.activeElement;

    this.focus({
      preventScroll: true
    });
  }

  returnFocusToPreviousElement() {
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  }
}

if (!customElements.get("account-drawer")) {
  customElements.define("account-drawer", AccountDrawer);
}
